/**
 * Bảng "Chưa đồng bộ được" — các op trong hàng đợi đã hỏng vì lỗi nghiệp vụ.
 *
 * Vì sao cần: `flush()` cố tình không thử lại op lỗi 4xx (quy tắc 4 của
 * farmer-offline-sync §3 — không quay vòng vô hạn). Nhưng trước đây chúng nằm lại
 * trong IndexedDB mà không có màn hình nào nhìn thấy, còn chỉ báo trên thanh
 * tiêu đề thì đếm cả chúng nên đứng nguyên "Đang đồng bộ (1 thay đổi)" mãi mãi.
 *
 * Tình huống thật đứng sau bảng này: điều tra viên nộp phiếu lúc mất sóng giữa
 * vườn, về tới chỗ có mạng thì máy chủ trả `AUDIT_BLOCKED` vì còn câu bắt buộc bỏ
 * trống. Họ phải thấy được điều đó, chứ không phải nhìn một con số đứng im và tin
 * rằng phiếu đã nộp xong.
 */
import { listFailed, retryFailed, discardFailed } from '../offline/queue.js';
import { el, esc, toast, formatDateTime } from './util.js';

const KIND_LABEL = {
  patchAnswers: 'Lưu câu trả lời',
  submit: 'Nộp phiếu',
  changeRequest: 'Đề xuất sửa',
};

/** Giải thích lỗi bằng tiếng người, kèm việc cần làm — mã lỗi thôi thì vô nghĩa. */
function explain(error, kind) {
  const code = error?.code;
  // Đề xuất sửa hỏng vì lý do khác hẳn phiếu: CONFLICT ở đây là "đã có người đề xuất
  // đúng trường này rồi", không phải phiếu bị khoá.
  if (kind === 'changeRequest') {
    if (code === 'CONFLICT') {
      return 'Đã có một đề xuất khác đang chờ duyệt cho thông tin này. Mở hồ sơ để xem; đề xuất này có thể bỏ qua.';
    }
    if (code === 'NOT_FOUND') return 'Hồ sơ không còn trong danh mục hoặc không thuộc địa bàn của bạn.';
    return error?.message || 'Máy chủ từ chối đề xuất này.';
  }
  if (code === 'AUDIT_BLOCKED') {
    return 'Phiếu còn câu bắt buộc chưa đạt điều kiện của bảng hỏi. Mở phiếu, điền nốt rồi nộp lại.';
  }
  if (code === 'CONFLICT') {
    return 'Phiếu đã bị khoá (có thể đã được nộp hoặc duyệt ở nơi khác). Nếu cần sửa, nhờ điều phối viên mở lại phiếu rồi bấm Thử lại.';
  }
  if (code === 'NOT_FOUND') {
    return 'Phiếu không còn tồn tại trên máy chủ — có thể đã bị xoá. Thay đổi này không gửi đi đâu được nữa.';
  }
  if (code === 'UNSUPPORTED_OP') {
    return 'Bản ghi cũ từ một phiên bản ứng dụng trước, phiên bản hiện tại không hiểu. Bỏ qua được.';
  }
  return error?.message || 'Máy chủ từ chối thay đổi này.';
}

export async function openSyncPanel() {
  const backdrop = el('div', 'dialog-backdrop');
  const box = el('div', 'dialog');
  backdrop.appendChild(box);
  document.body.appendChild(backdrop);

  const close = () => {
    backdrop.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e) => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  async function render() {
    const rows = await listFailed();

    box.innerHTML = `
      <div class="dialog-head">
        <h3>Chưa đồng bộ được</h3>
        <button class="icon-btn" type="button" data-act="close" aria-label="Đóng"><span class="ms">close</span></button>
      </div>
      <div class="dialog-body">
        ${
          rows.length
            ? `<p class="hint-text">Máy chủ từ chối những thay đổi dưới đây. Chúng vẫn được giữ tại máy —
                 xử lý nguyên nhân rồi bấm <b>Thử lại</b>, hoặc <b>Bỏ qua</b> nếu không cần nữa.</p>
               <ul class="sync-fail-list">${rows.map(itemHtml).join('')}</ul>`
            : `<div class="empty-state"><span class="ms ms-xl">cloud_done</span>
                 <p>Không còn thay đổi nào bị treo.</p></div>`
        }
      </div>
      <div class="dialog-actions">
        <button class="btn btn-text" type="button" data-act="close">Đóng</button>
      </div>`;

    box.querySelectorAll('[data-act="close"]').forEach((b) => b.addEventListener('click', close));

    box.querySelectorAll('[data-act="retry"]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        await retryFailed(Number(btn.dataset.id));
        // Đọc lại từ hàng đợi thay vì đoán kết quả: gửi lại có thể hỏng tiếp, và
        // lúc đó op phải hiện lại ở đây với lỗi mới.
        await render();
      }),
    );

    box.querySelectorAll('[data-act="discard"]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        const row = rows.find((r) => r.id === Number(btn.dataset.id));
        const what = KIND_LABEL[row?.kind] || 'Thay đổi';
        const forWhat = row?.kind === 'changeRequest' ? 'này' : 'cho phiếu này';
        if (!window.confirm(`Bỏ qua "${what}" ${forWhat}?\n\nDữ liệu trong thay đổi đó sẽ mất hẳn.`)) return;
        await discardFailed(Number(btn.dataset.id));
        toast('Đã bỏ qua thay đổi');
        await render();
      }),
    );
  }

  function itemHtml(op) {
    const answerCount = Object.keys(op.payload?.answers || {}).length;
    const isProposal = op.kind === 'changeRequest';
    const link = isProposal
      ? op.link ? `<a href="${esc(op.link)}">Mở hồ sơ</a>` : ''
      : `<a href="#/submissions/${esc(op.submissionId)}">Mở phiếu</a>`;
    const extra =
      op.kind === 'patchAnswers' && answerCount
        ? ` · ${answerCount} ô dữ liệu`
        : isProposal && op.payload?.items?.length
          ? ` · ${op.payload.items.length} trường`
          : '';
    return `
      <li class="sync-fail-item">
        <div class="sync-fail-main">
          <span class="badge">${esc(KIND_LABEL[op.kind] || op.kind)}</span>
          ${link}
          <span class="hint-text">${esc(formatDateTime(op.createdAt))}${extra}</span>
        </div>
        <p class="sync-fail-why">${esc(explain(op.error, op.kind))}</p>
        ${op.error?.code ? `<p class="hint-text mono">${esc(op.error.code)}${op.error.status ? ` · HTTP ${op.error.status}` : ''}</p>` : ''}
        <div class="sync-fail-actions">
          <button class="btn btn-tonal sm" type="button" data-act="retry" data-id="${op.id}">Thử lại</button>
          <button class="btn btn-text sm danger" type="button" data-act="discard" data-id="${op.id}">Bỏ qua</button>
        </div>
      </li>`;
  }

  await render();
}
